import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { LazyComponent } from 'ha/components.legacy/LazyComponent';
import { H3 } from 'ha/ui.legacy/Headers';
import { ContentfulImg } from 'ha/modules/Contentful/components/ContentfulImg';
import { Link } from 'ha/ui.legacy/Link';
import { buildContentfulAssetUrl } from 'ha/utils/buildContentfulAssetUrl';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { units } from '@ha/dsl';
import { useMediaQuery } from 'ha/helpers/MediaQuery';

import { useSelector } from 'react-redux';
import { getCurrentLanguageCode } from 'ha/modules/LanguageSwitcher/selectors';
import { ContentfulImage } from 'ha/modules/Hermes/types';

const IMAGE_ASSET_HEIGHT = 800;
const IMAGE_ASSET_WIDTH = 800;

const useStyles = makeStyles()({
  image: {
    height: units.rem(15),
  },
});

interface Props {
  title: string;
  description: string;
  image: string;
  imageAsset: ContentfulImage;
  linkUrl: string;
  kind?: 'primary' | 'featured' | 'default';
}

const backgroundImage = (url: string) => {
  return `linear-gradient(to bottom, rgba(0,0,0,0) 25%, rgba(0,0,0,0.5)), url(${buildContentfulAssetUrl(
    url,
    {
      width: IMAGE_ASSET_WIDTH,
      height: IMAGE_ASSET_HEIGHT,
    },
  )})`;
};

const CommunityCard: React.FC<Props> = ({
  kind = 'default',
  image: img,
  imageAsset,
  linkUrl,
  title,
  description,
}) => {
  const { classes, cx } = useStyles();
  const { md } = useMediaQuery();
  const lang = useSelector(getCurrentLanguageCode);

  const image = imageAsset || { fields: { url: img } };
  const desc = image.fields.description;
  const alt = desc ? desc[lang] || desc.en : '';

  return (
    <Track name="Clicked on community card" type="link">
      <Link to={linkUrl} skipLangFormat>
        <LazyComponent
          bottomOffset="-50%"
          render={({ hasEntered }) => (
            <div
              className="border-outline community-card flexbox flex-direction__column"
              style={{
                backgroundImage:
                  kind !== 'default' && hasEntered
                    ? backgroundImage((image as ContentfulImage).fields.url)
                    : undefined,
              }}
            >
              {kind === 'default' && hasEntered && (
                <div className={classes.image}>
                  <ContentfulImg
                    className="img-cover-container community-card--image"
                    src={(image as ContentfulImage).fields.url}
                    alt={alt}
                    // after lg, card size stays constant
                    sizes={{ xs: '100vw', md: '50vw', lg: '350px' }}
                  />
                </div>
              )}
              <div
                className={cx({
                  'm-x-1 m-y-1': kind === 'default',
                  'm-x-1_5 m-t-2_5 m-b-2_5': kind !== 'default',
                })}
              >
                <H3
                  title={title}
                  className={cx('m-b-1', {
                    'community-card--limit-text community-card--title__default color-base-dark':
                      kind === 'default',
                    'community-card--title__featured color-base-dark':
                      kind === 'featured' || (kind === 'primary' && !md),
                    'community-card--title__primary color-danger':
                      kind === 'primary' && md,
                  })}
                >
                  <span
                    className={cx({
                      'back-white': kind !== 'default',
                      'p-a-0_5':
                        kind === 'featured' || (kind === 'primary' && !md),
                      'p-a-0_75': kind === 'primary' && md,
                    })}
                  >
                    {title}
                  </span>
                </H3>
                <div
                  title={description}
                  className={cx(
                    'community-card--limit-text community-card--description',
                    {
                      'color-white': kind !== 'default',
                    },
                  )}
                >
                  {description}
                </div>
              </div>
            </div>
          )}
        />
      </Link>
    </Track>
  );
};

export { CommunityCard };
